import * as React from 'react';
import styled from '@emotion/styled';

import { config } from '../../config';
import { __ } from '../../helpers/TranslationService';

import { CheckmarkIcon } from '../Icons/newIcons/CheckmarkIcon';
import { CheckMarkRoundedIcon } from '../Icons/newIcons/CheckMarkRoundedIcon';

import { daysFromNow } from '../../helpers/priceHelpers';

import {
    softBlue,
    labels,
    BOOKING_TYPE_DIRECT,
    BOOKING_TYPE_INDIRECT,
} from '../../consts/variables';
import { BreakpointsEnum } from '../../helpers/breakpoints';

const StyledRequestFormButtonsV2 = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 15px;

    @media (min-width: ${BreakpointsEnum.sm}) {
        padding: 0 34px;
    }
`;

const StyledButtonsContainer = styled.div<{ isSingleButton: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 38px;
    align-self: auto;

    @media (min-width: ${BreakpointsEnum.sm}) {
        align-self: ${(props) => (props.isSingleButton ? 'center' : 'auto')};
        flex-direction: row;
    }
`;

const StyledButtonSection = styled.div<{
    type: 'direct' | 'non-binding';
}>`
    flex: 1;

    ul {
        li {
            margin-top: 16px;
            display: flex;
            align-items: center;

            span {
                margin: 0 8px;
            }
        }
    }

    svg {
        fill: ${({ type, theme }) =>
            type === 'direct'
                ? theme.productPage.requestButtons.primaryColor
                : theme.productPage.requestButtons.secondaryColor};
    }

    b {
        font-weight: normal;
    }

    button {
        margin: 0;
        max-width: 100%;
        border-radius: 6px;
        padding: 12px 0;
        font-size: 16px;
        line-height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        color: ${({ theme }) => theme.button.primary.text}!important;
        background-color: ${({ type, theme }) =>
            type === 'direct'
                ? theme.productPage.requestButtons.primaryColor
                : theme.productPage.requestButtons.secondaryColor}!important;
        > svg {
            fill: ${({ theme }) => theme.button.primary.text}!important;
        }
        &:hover {
            background-color: ${({ theme }) =>
                theme.productPage.requestButtons.hoverColor}!important;
        }
    }
`;

const StyledSSLLabel = styled.div`
    // we don't want to show this on mobile as per design
    @media (min-width: ${BreakpointsEnum.sm}) {
        display: flex;
    }

    display: none;
    align-items: center;
    align-self: center;

    img {
        width: 32px;
        margin-right: 8px;
    }
`;

interface IRequestFormButtonsV2 {
    isSubmitError?: boolean;
    validatePersonalForm: (requestType?: string) => void;
    apiValidationErrors?: { field: string; errorKey: string }[];
    isBelowThresholdPrice?: boolean;
    belowThresholdPriceMarkets?: boolean;
}

export const RequestFormButtonsV2 = (props: IRequestFormButtonsV2) => {
    const belowThresholdPriceEnabled =
        props.belowThresholdPriceMarkets && props.isBelowThresholdPrice;

    const isIndirectBookingAllowed = !belowThresholdPriceEnabled;

    const hasValidationErrors = props.apiValidationErrors && props.apiValidationErrors.length;

    return (
        <StyledRequestFormButtonsV2>
            {!!props.isSubmitError && !!hasValidationErrors && (
                <p className="submitError">
                    {__(props.apiValidationErrors[0].errorKey, 'dreamlines')}
                </p>
            )}

            {props.isSubmitError && !hasValidationErrors && (
                <p className="submitError">{__('Something went wrong', 'dreamlines')}</p>
            )}

            <StyledButtonsContainer isSingleButton={!isIndirectBookingAllowed}>
                {isIndirectBookingAllowed && (
                    <StyledButtonSection type="non-binding">
                        <button
                            className="blue_button darker_blue has_hover w-100"
                            onClick={() => props.validatePersonalForm(BOOKING_TYPE_INDIRECT)}
                        >
                            {__('confirmNonBindingBooking', 'dreamlines')}
                        </button>

                        <ul>
                            <li>
                                <CheckmarkIcon width="13" height="13" />
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: __('request.CTARequestButtonRow1', 'dreamlines'),
                                    }}
                                />
                            </li>
                            <li>
                                <CheckmarkIcon width="13" height="13" />
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: __('request.CTARequestButtonRow2', 'dreamlines'),
                                    }}
                                />
                            </li>
                        </ul>
                    </StyledButtonSection>
                )}

                <StyledButtonSection type="direct">
                    <button
                        className="blue_button has_hover w-100"
                        onClick={() => props.validatePersonalForm(BOOKING_TYPE_DIRECT)}
                    >
                        <CheckMarkRoundedIcon
                            style={{
                                fill: 'white',
                                width: '16px',
                                height: '16px',
                                marginRight: '6px',
                            }}
                        />
                        {__('request.directButtonText', 'dreamlines')}
                    </button>

                    <ul>
                        <li>
                            <CheckmarkIcon width="14" height="14" />
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `${__('request.CTADirectButtonRow1', 'dreamlines')}`,
                                }}
                            />
                        </li>
                        <li>
                            <CheckmarkIcon width="14" height="14" />
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: __('request.CTADirectButtonRow2', 'dreamlines'),
                                }}
                            />
                        </li>
                    </ul>
                </StyledButtonSection>
            </StyledButtonsContainer>

            <StyledSSLLabel>
                <img
                    src={`${config.images}/common/ssl.png`}
                    alt={__('request.sslText', 'dreamlines')}
                />
                <p>{__('request.sslText', 'dreamlines')}</p>
            </StyledSSLLabel>
        </StyledRequestFormButtonsV2>
    );
};
